import axios from "@/services/axios.config";

const TIMESHEET_LIST_PATH = "api/users/timesheets/get-timesheet/";

/** Timesheet list API may return a bare array or a paginated envelope. */
export function parseTimesheetListResponse(data) {
  if (Array.isArray(data))
    return {rows: data, paginated: false, hasMore: false};
  if (data && typeof data === "object") {
    if (Array.isArray(data.results)) {
      return {
        rows: data.results,
        paginated: true,
        hasMore: Boolean(data.next),
      };
    }
    if (Array.isArray(data.data)) {
      return {rows: data.data, paginated: false, hasMore: false};
    }
  }
  return {rows: [], paginated: false, hasMore: false};
}

function buildTimesheetListUrl(filterQuery, page) {
  const parts = [];
  const trimmed = String(filterQuery || "")
    .replace(/&$/, "")
    .trim();
  if (trimmed) parts.push(trimmed);
  parts.push(`page=${page}`);
  return `${TIMESHEET_LIST_PATH}?${parts.join("&")}`;
}

/**
 * Load all rows for a filter query. Uses paginated `page` requests when the API
 * returns `{ results, next }`; otherwise accepts a single legacy array response.
 */
export async function fetchAllFilteredTimesheets(filterQuery, options = {}) {
  const timeoutMs = options.timeoutMs != null ? options.timeoutMs : 180000;
  const onProgress = options.onProgress;
  const all = [];
  const maxPages = 250;

  for (let page = 1; page <= maxPages; page += 1) {
    const url = buildTimesheetListUrl(filterQuery, page);
    if (typeof onProgress === "function") {
      onProgress({page, loaded: all.length});
    }
    let response;
    try {
      response = await axios.get(url, {timeout: timeoutMs});
    } catch (err) {
      throw formatTimesheetFetchError(err, {page, loaded: all.length});
    }
    if (response.status !== 200) {
      throw new Error(`Timesheet filter failed (HTTP ${response.status}).`);
    }
    const parsed = parseTimesheetListResponse(response.data);
    all.push(...parsed.rows);
    if (!parsed.paginated) {
      return all;
    }
    if (!parsed.hasMore) {
      return all;
    }
  }
  throw new Error(
    `Timesheet filter returned too many pages (>${maxPages}). Narrow the date range or pick a project.`
  );
}

export function formatTimesheetFetchError(err, ctx = {}) {
  const pageHint =
    ctx.page != null
      ? ` (failed on page ${ctx.page}, ${ctx.loaded || 0} rows loaded)`
      : "";
  if (!err || !err.response) {
    const code = err && err.code;
    if (code === "ECONNABORTED") {
      return new Error(
        `Timesheet request timed out${pageHint}. Try a shorter date range, select a project, or retry.`
      );
    }
    return new Error(
      `Could not reach the timesheet API${pageHint}. Check your connection and retry.`
    );
  }
  const status = err.response.status;
  if (status === 504 || status === 502) {
    return new Error(
      `Server timed out building the timesheet report${pageHint}. Narrow the date range or select a project.`
    );
  }
  if (status === 400) {
    return new Error(
      `Invalid timesheet filter${pageHint}. Check project, member, and dates.`
    );
  }
  return new Error(`Timesheet filter failed (HTTP ${status})${pageHint}.`);
}

function rowIsoDate(row) {
  const raw = row && (row.log_date || row.timer_log_date || row.created_at);
  if (!raw) return "";
  return String(raw).slice(0, 10);
}

function rowInDateRange(row, startDate, endDate) {
  const iso = rowIsoDate(row);
  if (!iso) return false;
  if (startDate && iso < startDate) return false;
  if (endDate && iso > endDate) return false;
  return true;
}

function buildStructuredFilterQuery({
  projectId,
  userId,
  startDate,
  endDate,
  includeDates = true,
}) {
  const q = new URLSearchParams();
  if (projectId != null && projectId !== "") {
    q.set("project_id", String(projectId));
  }
  if (userId != null && userId !== "") {
    q.set("user_id", String(userId));
  }
  if (includeDates) {
    if (startDate) q.set("start_date", startDate);
    if (endDate) q.set("end_date", endDate);
  }
  const s = q.toString();
  return s ? `${s}&` : "";
}

export function resolveTimesheetProjectId(projectValue, projectNameList = []) {
  if (projectValue == null || projectValue === "") return null;
  if (typeof projectValue === "object") {
    if (projectValue.value != null) return projectValue.value;
    if (projectValue.id != null) return projectValue.id;
  }
  const asNum = Number(projectValue);
  if (Number.isFinite(asNum) && asNum > 0) return asNum;
  const name = String(projectValue).trim();
  const byName = (projectNameList || []).find(
    (p) => String(p.project_name || "").trim() === name
  );
  return byName ? byName.id : null;
}

export function resolveTimesheetUserId(userValue) {
  if (userValue == null || userValue === "") return null;
  if (typeof userValue === "object") {
    if (userValue.value != null) return userValue.value;
    if (userValue.id != null) return userValue.id;
  }
  const asNum = Number(userValue);
  return Number.isFinite(asNum) && asNum > 0 ? asNum : null;
}

/**
 * Structured filter for View Timesheet. Retries without date params when the
 * scoped query returns nothing, then applies dates client-side.
 */
export async function fetchFilteredTimesheets({
  projectId = null,
  userId = null,
  startDate = "",
  endDate = "",
} = {}) {
  const hasDates = Boolean(startDate || endDate);
  let rows = await fetchAllFilteredTimesheets(
    buildStructuredFilterQuery({
      projectId,
      userId,
      startDate,
      endDate,
      includeDates: true,
    })
  );

  if (!rows.length && hasDates) {
    const broad = await fetchAllFilteredTimesheets(
      buildStructuredFilterQuery({projectId, userId, includeDates: false})
    );
    rows = broad.filter((row) => rowInDateRange(row, startDate, endDate));
  }

  return rows;
}
